import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { colors } from '@/constants/colors';

// 'agent-down' (Lite only): the WG tunnel handshakes fine — the box has
// power + internet — but the SmartFi agent stopped heartbeating. Coins are
// accepted while paid customers get no internet, so it needs its own color:
// the fix is a reboot, not an ISP/power check.
export type RouterStatus =
   | 'connected'
   | 'agent-down'
   | 'disconnected'
   | 'unprovisioned';

interface Props {
   status: RouterStatus;
   compact?: boolean;
   title?: string;
}

const RouterPill: React.FC<Props> = ({ status, compact = false, title }) => {
   const label =
      status === 'connected'
         ? 'Connected'
         : status === 'agent-down'
         ? 'Agent down'
         : status === 'disconnected'
         ? 'Disconnected'
         : 'Not provisioned';
   const defaultTitle =
      status === 'connected'
         ? 'Router reachable (heartbeat fresh within 3 min)'
         : status === 'agent-down'
         ? 'Router online (tunnel alive) but the SmartFi agent stopped — customers may pay without getting internet. Reboot the router.'
         : status === 'disconnected'
         ? 'Router unreachable (no heartbeat in 3+ min)'
         : 'Router never provisioned (no WG peer minted yet)';
   return (
      <Pill $status={status} title={title || defaultTitle}>
         <Dot $status={status} />
         {!compact && <span>{label}</span>}
      </Pill>
   );
};

export default RouterPill;

const pulse = keyframes`
   0% { transform: scale(1); opacity: 1; }
   50% { transform: scale(1.25); opacity: 0.55; }
   100% { transform: scale(1); opacity: 1; }
`;

const ring = keyframes`
   0% { transform: scale(1); opacity: 0.6; }
   100% { transform: scale(2.4); opacity: 0; }
`;

const Pill = styled.span<{ $status: RouterStatus }>`
   display: inline-flex;
   align-items: center;
   gap: 7px;
   padding: 3px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 600;
   line-height: 1.4;
   background: ${({ $status }) =>
      $status === 'connected'
         ? `${colors.SUCCESS}1f`
         : $status === 'agent-down'
         ? `${colors.WARNING}26`
         : $status === 'disconnected'
         ? `${colors.DANGER}1f`
         : `${colors.GRAY10}40`};
   color: ${({ $status }) =>
      $status === 'connected'
         ? colors.SUCCESS
         : $status === 'agent-down'
         ? colors.WARNING
         : $status === 'disconnected'
         ? colors.DANGER
         : colors.GRAY5};
   white-space: nowrap;
`;

const Dot = styled.span<{ $status: RouterStatus }>`
   position: relative;
   width: 8px;
   height: 8px;
   border-radius: 50%;
   flex-shrink: 0;
   background: ${({ $status }) =>
      $status === 'connected'
         ? colors.SUCCESS
         : $status === 'agent-down'
         ? colors.WARNING
         : $status === 'disconnected'
         ? colors.DANGER
         : 'transparent'};
   border: ${({ $status }) =>
      $status === 'unprovisioned' ? `1.5px solid ${colors.GRAY5}` : 'none'};
   animation: ${({ $status }) =>
      $status === 'connected' ? css`${pulse} 1.6s ease-in-out infinite` : 'none'};

   /* Outer ripple for the connected state */
   ${({ $status }) =>
      $status === 'connected' &&
      css`
         &::after {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: ${colors.SUCCESS};
            animation: ${ring} 1.6s ease-out infinite;
         }
      `}
`;
